import { useCallback, useEffect, useState } from 'react';

/**
 * Which facet groups the rail draws, persisted per module.
 *
 * A module can carry more axes than any one reader uses — Status, Unit and Vacancy on the
 * plantilla — and a rail scrolling past the ones they never touch costs them the ones they
 * do. This is the rail's own version of the index cog's column settings: a layout
 * preference, not a filter.
 *
 * **Hiding is presentation only, and it can never hide an active filter.** The stored set is
 * what the reader asked to put away; `isVisible` overrules it for any axis currently carrying
 * a constraint. That is what makes the preference safe to persist — no arrangement of stored
 * state and later clicks can produce a list narrowed by something the reader cannot see.
 *
 * Deriving it rather than clearing the axis on hide is the whole point. Clearing would make a
 * layout control destructive; storing "hidden" and then honouring it unconditionally would
 * make an invisible filter possible the moment another control writes that axis — and one
 * does, because the status folder nav and the rail's Status facet are two views of the same
 * state.
 */
function storageKey(key: string): string {
  return `index.${key}.hiddenFacets`;
}

function read(key: string): string[] {
  try {
    const stored = localStorage.getItem(storageKey(key));
    if (stored === null) return [];

    const parsed: unknown = JSON.parse(stored);

    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    // Blocked storage, or a value written by an older shape — show everything.
    return [];
  }
}

export function useFacetVisibility(key: string) {
  const [hidden, setHidden] = useState<string[]>(() => read(key));

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(key), JSON.stringify(hidden));
    } catch {
      // Persisting the layout is a convenience, never a requirement.
    }
  }, [key, hidden]);

  const toggle = useCallback((facetKey: string) => {
    setHidden((current) =>
      current.includes(facetKey) ? current.filter((k) => k !== facetKey) : [...current, facetKey],
    );
  }, []);

  /**
   * Whether the rail draws this facet.
   *
   * `constrained` is the override: an axis the reader is filtering on stays on screen
   * whatever the stored preference says, because the alternative is a list narrowed by
   * something with no control anywhere to show it.
   */
  const isVisible = useCallback(
    (facetKey: string, constrained: boolean) => constrained || !hidden.includes(facetKey),
    [hidden],
  );

  return { hidden, toggle, isVisible };
}
